import React, { useState, useEffect } from "react";
import { Modal, Spin, DatePicker, message } from "antd";
import { getWorkerStats } from "../../services/service";
import dayjs from "dayjs";

const WorkerStatsModal = ({ visible, onClose, worker }) => {
  const [loading, setLoading] = useState(false);
  const [statsData, setStatsData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(dayjs());

  useEffect(() => {
    if (visible && worker?.id) {
      // Reset to today's date when modal opens
      setSelectedDate(dayjs());
      fetchWorkerStats(dayjs().format('YYYY-MM-DD'));
    } else if (!visible) {
      // Clear data when modal closes
      setStatsData(null);
      setSelectedDate(dayjs());
    }
  }, [visible, worker]);

  const fetchWorkerStats = async (date) => {
    setLoading(true);
    try {
      // GET /worker/stats?worker_id=:id&date=:date
      const response = await getWorkerStats(worker.id, date);
      console.log('Worker Stats Response:', response);
      if (response?.success) {
        setStatsData(response.data);
      } else {
        setStatsData(null);
      }
    } catch (error) {
      console.error("Error fetching worker stats:", error);
      message.error("Failed to load worker stats");
      setStatsData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (date) => {
    if (date) {
      setSelectedDate(date);
      fetchWorkerStats(date.format('YYYY-MM-DD'));
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return timeString;
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime) return 'N/A';
    const parsed = new Date(dateTime);
    if (Number.isNaN(parsed.getTime())) return 'N/A';
    const timeZone = statsData?.local_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    return new Intl.DateTimeFormat("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone,
    }).format(parsed);
  };

  const getActivityBadgeColor = (activity) => {
    switch (activity) {
      case 'CHECKED':
        return 'bg-green-100 text-green-800';
      case 'BREAK':
        return 'bg-orange-100 text-[#F97316]';
      case 'TRAVEL':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Modal
      title={
        <div className="flex items-center gap-3">
          <span className="text-[18px] font-semibold text-[#1E3A8A]">
            Worker Stats - {worker?.username}
          </span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
    >
      <div className="mb-4">
        <label className="block text-[14px] font-medium text-[#4B5563] mb-2">
          Select Date
        </label>
        <DatePicker
          value={selectedDate}
          onChange={handleDateChange}
          format="YYYY-MM-DD"
          className="w-full"
          disabledDate={(current) => current && current > dayjs().endOf('day')}
        />
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Spin size="large" />
        </div>
      ) : statsData && statsData.details && statsData.details.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-[#F8F9FB]">
                <th className="text-left py-3 px-4 font-semibold text-[14px] text-black border-b border-[#E9E9E9] whitespace-nowrap">
                  Start Time
                </th>
                <th className="text-left py-3 px-4 font-semibold text-[14px] text-black border-b border-[#E9E9E9] whitespace-nowrap">
                  End Time
                </th>
                <th className="text-left py-3 px-4 font-semibold text-[14px] text-black border-b border-[#E9E9E9] whitespace-nowrap">
                  Activity Type
                </th>
                <th className="text-left py-3 px-4 font-semibold text-[14px] text-black border-b border-[#E9E9E9] whitespace-nowrap">
                  Check In Time
                </th>
                <th className="text-left py-3 px-4 font-semibold text-[14px] text-black border-b border-[#E9E9E9] whitespace-nowrap">
                  Check Out Time
                </th>
                <th className="text-left py-3 px-4 font-semibold text-[14px] text-black border-b border-[#E9E9E9] whitespace-nowrap">
                  Total Check Ins
                </th>
                <th className="text-left py-3 px-4 font-semibold text-[14px] text-black border-b border-[#E9E9E9] whitespace-nowrap">
                  Total Breaks
                </th>
                <th className="text-left py-3 px-4 font-semibold text-[14px] text-black border-b border-[#E9E9E9] whitespace-nowrap">
                  Total Travels
                </th>
                <th className="text-left py-3 px-4 font-semibold text-[14px] text-black border-b border-[#E9E9E9] whitespace-nowrap">
                  Total Break Time
                </th>
                <th className="text-left py-3 px-4 font-semibold text-[14px] text-black border-b border-[#E9E9E9] whitespace-nowrap">
                  Total Travel Time
                </th>
                <th className="text-left py-3 px-4 font-semibold text-[14px] text-black border-b border-[#E9E9E9] whitespace-nowrap">
                  Total Working Time
                </th>
                <th className="text-left py-3 px-4 font-semibold text-[14px] text-black border-b border-[#E9E9E9] whitespace-nowrap">
                  Activity
                </th>
              </tr>
            </thead>
            <tbody>
              {statsData.details.map((detail, index) => (
                <tr
                  key={detail.id || index}
                  className="hover:bg-[#F8F9FB] transition-colors"
                >
                  <td className="py-3 px-4 border-b border-[#E9E9E9] text-[14px] text-[#4B5563] whitespace-nowrap">
                    {formatDateTime(detail.start_time)}
                  </td>
                  <td className="py-3 px-4 border-b border-[#E9E9E9] text-[14px] text-[#4B5563] whitespace-nowrap">
                    {formatDateTime(detail.end_time)}
                  </td>
                  <td className="py-3 px-4 border-b border-[#E9E9E9] text-[14px] text-[#4B5563] whitespace-nowrap">
                    {detail.activityType || 'N/A'}
                  </td>
                  <td className="py-3 px-4 border-b border-[#E9E9E9] text-[14px] text-[#4B5563] whitespace-nowrap">
                    {formatDateTime(statsData.checkInTime)}
                  </td>
                  <td className="py-3 px-4 border-b border-[#E9E9E9] text-[14px] text-[#4B5563] whitespace-nowrap">
                    {formatDateTime(statsData.checkOutTime)}
                  </td>
                  <td className="py-3 px-4 border-b border-[#E9E9E9] text-[14px] text-[#4B5563] whitespace-nowrap">
                    {statsData.totalCheckIns || 0}
                  </td>
                  <td className="py-3 px-4 border-b border-[#E9E9E9] text-[14px] text-[#4B5563] whitespace-nowrap">
                    {statsData.totalBreaks || 0}
                  </td>
                  <td className="py-3 px-4 border-b border-[#E9E9E9] text-[14px] text-[#4B5563] whitespace-nowrap">
                    {statsData.totalTravels || 0}
                  </td>
                  <td className="py-3 px-4 border-b border-[#E9E9E9] text-[14px] text-[#4B5563] whitespace-nowrap">
                    {formatTime(statsData.totalBreakTime)}
                  </td>
                  <td className="py-3 px-4 border-b border-[#E9E9E9] text-[14px] text-[#4B5563] whitespace-nowrap">
                    {formatTime(statsData.totalTravelTime)}
                  </td>
                  <td className="py-3 px-4 border-b border-[#E9E9E9] text-[14px] text-[#4B5563] whitespace-nowrap">
                    {formatTime(statsData.totalWorkingTime)}
                  </td>
                  <td className="py-3 px-4 border-b border-[#E9E9E9]">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getActivityBadgeColor(detail.activity)}`}>
                      {detail.activity}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12 text-[#B7B7B7]">
          No activity found for the specified date range.
        </div>
      )}
    </Modal>
  );
};

export default WorkerStatsModal;
